import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toTitleCasePt } from "@/lib/format";
import { EstadoTag, AtoTag, ItemEvento, ItemProcesso, NovidadeTag, NOME_RESULTADO, dataBR, dataHoraBR } from "@/components/colab/colabUi";
import ColabInteracao from "@/components/colab/ColabInteracao";

// Lote 161 — o cartão do que foi compartilhado COM você. Serve a conta do IApauta (base "/colab/recebidos") e o convidado sem conta
// (base "/externo/compartilhamentos"): mesma tela, mesmas regras; o convidado não tem agenda no app.
export const msgErro = (e, padrao) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : padrao);

// Acesso e ato são decisões separadas: aceitar o convite dá acesso para ver; só o botão "Aceitar o ato" assume a tarefa.
function PainelAto({ r, base, agir, busy }) {
  const [motivo, setMotivo] = useState("");
  const [resultado, setResultado] = useState("");
  const [obs, setObs] = useState("");
  const est = r.ato_estado;
  const a = r.ato || {};
  const url = (acao) => `${base}/${r.share_id}/ato/${acao}`;
  return (
    <div data-testid={`ato-${r.share_id}`} className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">Ato solicitado</span>
        <AtoTag estado={est} rotulo={r.ato_rotulo} testid={`ato-estado-${r.share_id}`} />
      </div>
      {r.instrucao && <p className="text-sm text-stone-900" data-testid={`ato-instrucao-${r.share_id}`}>{r.instrucao}</p>}

      {est === "aguardando_resposta" && (
        <div className="space-y-2">
          <p className="text-xs text-stone-600">Aceitar o acesso não é aceitar o ato. Responda se você assume esta tarefa.</p>
          <Textarea data-testid={`ato-motivo-${r.share_id}`} rows={2} maxLength={1000} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Se não puder, diga o motivo (opcional)" />
          <div className="flex gap-2 flex-wrap">
            <Button data-testid={`ato-aceitar-${r.share_id}`} disabled={busy} onClick={() => agir(url("aceitar"), {}, "Ato aceito")} className="bg-stone-900 hover:bg-stone-800 text-white">Aceitar o ato</Button>
            <Button data-testid={`ato-recusar-${r.share_id}`} disabled={busy} variant="outline" onClick={() => agir(url("recusar"), { observacao: motivo }, "Ato recusado")}>Não posso</Button>
          </div>
        </div>
      )}

      {est === "reconfirmar" && (
        <div className="space-y-2" data-testid={`ato-reconfirmar-${r.share_id}`}>
          <p className="text-sm text-amber-900 rounded-md border border-amber-200 bg-amber-50 p-2">Os eventos mudaram (data, hora ou cancelamento) depois do seu aceite. Confira acima e confirme de novo para continuar.</p>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid={`ato-reconfirmar-ok-${r.share_id}`} disabled={busy} onClick={() => agir(url("reconfirmar"), {}, "Confirmado de novo")} className="bg-stone-900 hover:bg-stone-800 text-white">Confirmar de novo</Button>
            <Button data-testid={`ato-recusar-${r.share_id}`} disabled={busy} variant="outline" onClick={() => agir(url("recusar"), { observacao: "Não posso após a mudança" }, "Ato recusado")}>Não posso mais</Button>
          </div>
        </div>
      )}

      {est === "aceito" && (
        <div className="space-y-2">
          {a.conferencia && a.conferencia.ok === false && (
            <p data-testid={`ato-nota-${r.share_id}`} className="text-sm text-amber-900 rounded-md border border-amber-200 bg-amber-50 p-2">Devolvido por quem pediu: {a.conferencia.nota}</p>
          )}
          <div className="text-xs font-semibold text-stone-700">Informar a realização</div>
          <div className="flex gap-2 flex-wrap" role="group" aria-label="Resultado">
            {Object.entries(NOME_RESULTADO).map(([v, l]) => (
              <button key={v} type="button" data-testid={`ato-resultado-${v}-${r.share_id}`} aria-pressed={resultado === v} onClick={() => setResultado(v)}
                className={`h-9 px-3 rounded-md border text-sm ${resultado === v ? "bg-stone-400 border-stone-400 text-white" : "bg-white border-stone-300 text-stone-800"}`}>{l}</button>
            ))}
          </div>
          <Textarea data-testid={`ato-obs-${r.share_id}`} rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (obrigatória se não foi realizado). Anexos de comprovante chegam em uma próxima versão." />
          <div className="flex gap-2 flex-wrap">
            <Button data-testid={`ato-informar-${r.share_id}`} disabled={busy || !resultado} onClick={() => agir(url("realizacao"), { resultado, observacao: obs }, "Realização informada")} className="bg-stone-900 hover:bg-stone-800 text-white">Informar realização</Button>
            <Button data-testid={`ato-recusar-${r.share_id}`} disabled={busy} variant="ghost" className="text-stone-600" onClick={() => agir(url("recusar"), { observacao: obs || "Não posso mais" }, "Ato recusado")}>Desistir do ato</Button>
          </div>
        </div>
      )}

      {est === "realizacao_informada" && (
        <p className="text-sm text-stone-700" data-testid={`ato-aguarda-${r.share_id}`}>
          Você informou: <b>{a.realizacao?.rotulo}</b>{a.realizacao?.observacao ? ` — ${a.realizacao.observacao}` : ""}. Aguardando a conferência de {r.remetente_nome}.
        </p>
      )}
      {est === "conferido" && <p className="text-sm text-emerald-900" data-testid={`ato-conferido-${r.share_id}`}>Realização conferida por {r.remetente_nome}{a.conferencia?.quando ? ` em ${dataBR(a.conferencia.quando)}` : ""}.</p>}
      {est === "recusado" && <p className="text-sm text-stone-700">Você recusou este ato{a.motivo ? `: ${a.motivo}` : ""}.</p>}
      {est === "cancelado" && <p className="text-sm text-stone-700">Este ato foi cancelado porque o acesso terminou.</p>}
    </div>
  );
}

export function CartaoRecebido({ r, base = "/colab/recebidos", convidado = false, aberto, onAlternar, onMudou }) {
  const [conteudo, setConteudo] = useState(null);
  const [mostrar, setMostrar] = useState(false);
  const [busy, setBusy] = useState(false);
  const ativo = r.estado === "ativo";
  const ehAto = r.finalidade === "ato";
  const ehProc = r.objeto === "processo";
  const nome = (n) => (ehProc ? (n === 1 ? "processo" : "processos") : (n === 1 ? "evento" : "eventos"));
  useEffect(() => {
    if (!aberto || !ativo) return;
    http.get(`${base}/${r.share_id}/conteudo`).then((x) => setConteudo(x.data)).catch((e) => setConteudo({ erro: msgErro(e, "Não foi possível abrir.") }));
  }, [aberto, ativo, r.share_id, r.ato_estado, r.novidades, base]);

  const agir = async (url, corpo, ok) => {
    setBusy(true);
    try { await http.post(url, corpo); toast.success(ok); await onMudou(); }
    catch (e) { toast.error(msgErro(e, "Não foi possível concluir")); await onMudou(); } finally { setBusy(false); }
  };
  const alternarAgenda = async (v) => {
    setBusy(true);
    try { await http.put(`${base}/${r.share_id}/agenda`, { mostrar: v }); toast.success(v ? "Passou a aparecer na sua agenda" : "Saiu da sua agenda"); await onMudou(); }
    catch (e) { toast.error(msgErro(e, "Não foi possível alterar")); } finally { setBusy(false); }
  };

  return (
    <li data-testid={`rec-${r.share_id}`} className="bg-white border border-stone-200 rounded-md">
      <button type="button" onClick={onAlternar} aria-expanded={aberto} data-testid={`rec-abrir-${r.share_id}`} className="w-full text-left px-4 py-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold text-stone-900 leading-snug">{toTitleCasePt(r.remetente_nome)}</span>
            <EstadoTag estado={r.estado} rotulo={r.estado_rotulo} testid={`rec-estado-${r.share_id}`} />
            {ehAto && r.ato_estado && <AtoTag estado={r.ato_estado} rotulo={r.ato_rotulo} testid={`rec-ato-${r.share_id}`} />}
            <NovidadeTag n={r.novidades} testid={`rec-novidades-${r.share_id}`} />
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            {r.org_nome ? `Escritório ${r.org_nome} · ` : ""}{r.n_itens} {nome(r.n_itens)} · {r.finalidade_rotulo}
            {ativo ? ` · acesso até ${dataBR(r.acesso_ate)}` : r.estado === "aguardando_aceite" ? ` · convite vale até ${dataBR(r.convite_expira_em)}` : ""}
          </div>
        </div>
        {aberto ? <ChevronUp className="w-4 h-4 text-stone-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-stone-400 mt-1" />}
      </button>
      {aberto && (
        <div className="border-t border-stone-100 px-4 py-3 space-y-3" data-testid={`rec-det-${r.share_id}`}>
          {r.mensagem && <div className="text-xs italic text-stone-600">“{r.mensagem}”</div>}
          {r.estado === "aguardando_aceite" && (
            <>
              {ehAto && r.instrucao && <div className="text-sm"><span className="text-[11px] font-bold uppercase tracking-widest text-stone-500 block">O que se espera</span>{r.instrucao}</div>}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">{ehProc ? "Antes de aceitar você vê só o final do número do processo" : "Antes de aceitar você vê só tipo, data e hora"}</div>
                <ul className="space-y-0.5 text-sm" data-testid={`rec-resumo-${r.share_id}`}>
                  {ehProc
                    ? r.resumo.map((x) => <li key={x.ref}><ItemProcesso it={x} /></li>)
                    : r.resumo.map((x) => <li key={x.ref}><b>{x.titulo}</b> <span className="text-stone-600 tabular-nums">{dataBR(x.data)}{x.hora ? ` às ${x.hora}` : ""}</span></li>)}
                </ul>
              </div>
              {ehProc ? (
                <p className="text-xs text-stone-600" data-testid={`rec-categorias-${r.share_id}`}>
                  Ao aceitar o convite você poderá <b>acompanhar</b> {r.n_itens === 1 ? "este processo" : "estes processos"} nas categorias liberadas: <b>{(r.categorias_rotulos || []).join(", ")}</b>. Você poderá comentar e propor alterações, mas <b>não altera nada diretamente</b>: o remetente é avisado e decide. Não dá acesso a mais nada na conta dele.
                </p>
              ) : (
                <p className="text-xs text-stone-600">
                  Ao aceitar o convite você poderá <b>consultar</b> estes eventos (partes, número do processo, vara e comarca). Não dá acesso a mais nada na conta de quem compartilhou.
                  {ehAto && <> <b>Aceitar o convite não é aceitar o ato</b>: isso você decide depois, separadamente.</>}
                </p>
              )}
              {!convidado && !ehProc && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`rec-mostrar-${r.share_id}`} checked={mostrar} onChange={(e) => setMostrar(e.target.checked)} className="h-4 w-4 accent-stone-900" />
                  Mostrar na minha agenda (somente leitura)
                </label>
              )}
              <div className="flex gap-2">
                <Button data-testid={`rec-aceitar-${r.share_id}`} disabled={busy} onClick={() => agir(`${base}/${r.share_id}/aceitar`, convidado ? undefined : { mostrar_na_agenda: mostrar }, "Convite aceito")} className="bg-stone-900 hover:bg-stone-800 text-white">Aceitar convite</Button>
                <Button data-testid={`rec-recusar-${r.share_id}`} disabled={busy} variant="outline" onClick={() => agir(`${base}/${r.share_id}/recusar`, undefined, "Convite recusado")}>Recusar</Button>
              </div>
            </>
          )}
          {ativo && (
            !conteudo ? <div className="text-sm text-stone-500">Carregando…</div> : conteudo.erro ? (
              <div className="text-sm text-rose-800" data-testid={`rec-erro-${r.share_id}`}>{conteudo.erro}</div>
            ) : (
              <>
                <ul className="space-y-2">
                  {conteudo.itens.map((it) => <li key={it.ref}>{ehProc ? <ItemProcesso it={it} testid={`rec-item-${r.share_id}-${it.ref}`} /> : <ItemEvento it={it} detalhado testid={`rec-item-${r.share_id}-${it.ref}`} />}</li>)}
                </ul>
                <p className="text-[11px] text-stone-500">Os dados são lidos de {r.remetente_nome} a cada abertura e podem mudar.</p>
                {ehAto && <PainelAto r={r} base={base} agir={agir} busy={busy} />}
                {ehProc && (
                  <ColabInteracao sid={r.share_id} base={`${base}/${r.share_id}`} lado="parceiro" remetente={r.remetente_nome} onMudou={onMudou}
                    eventos={conteudo.itens.flatMap((it) => it.eventos || [])} />
                )}
                {!convidado && !ehProc && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid={`rec-agenda-${r.share_id}`} checked={r.mostrar_na_agenda} disabled={busy} onChange={(e) => alternarAgenda(e.target.checked)} className="h-4 w-4 accent-stone-900" />
                    Mostrar na minha agenda
                  </label>
                )}
                <Button variant="outline" size="sm" data-testid={`rec-sair-${r.share_id}`} disabled={busy}
                  onClick={() => { if (window.confirm(ehProc ? "Sair deste acesso? Você deixa de ver estes processos." : "Sair deste acesso? Você deixa de ver estes eventos.")) agir(`${base}/${r.share_id}/sair`, undefined, "Você saiu deste acesso"); }}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50">Sair deste acesso</Button>
              </>
            )
          )}
          {!ativo && r.estado !== "aguardando_aceite" && (
            <p className="text-xs text-stone-500" data-testid={`rec-fim-${r.share_id}`}>
              {{ recusado: "Você recusou este convite.", convite_expirado: "O convite expirou. Peça um novo a quem compartilhou.", acesso_expirado: "O prazo de acesso terminou.", encerrado: "O acesso foi encerrado." }[r.estado]} {ehProc ? "O processo" : "Os eventos"} não {ehProc ? "está" : "estão"} mais {ehProc ? "disponível" : "disponíveis"}.
              {ehAto && r.ato_estado === "conferido" ? " O resultado do ato continua registrado." : ""}
            </p>
          )}
          {r.aceito_em && <div className="text-[11px] text-stone-400">Aceito em {dataHoraBR(r.aceito_em)}</div>}
        </div>
      )}
    </li>
  );
}
